import { useState, useCallback, useRef, useEffect } from "react";
import "./HeroSectionPages.scss";

interface Slide {
  id: number;
  imgDesktop: string;
  imgMobile: string;
  imgAlt: string;
  quote: string;
  name: string;
  role: string;
  ctaLabel: string;
  ctaHref: string;
}

const slides: Slide[] = [
  {
    id: 0,
    imgDesktop:
      "https://images.unsplash.com/photo-1573164713988-8665fc963095?w=2000&auto=format&fit=crop&q=80",
    imgMobile:
      "https://images.unsplash.com/photo-1573164713988-8665fc963095?w=750&auto=format&fit=crop&q=80",
    imgAlt:
      "Enterprise software engineer reviewing system architecture on multiple screens in a Lagos tech hub.",
    quote:
      "CILPRON's custom software eliminated our transaction bottlenecks overnight. Our platform now processes 10× the volume with zero downtime.",
    name: "Adaeze Okonkwo",
    role: "CTO, FinEdge Nigeria",
    ctaLabel: "Explore custom software",
    ctaHref: "#",
  },
  {
    id: 1,
    imgDesktop:
      "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=2000&auto=format&fit=crop&q=80",
    imgMobile:
      "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=750&auto=format&fit=crop&q=80",
    imgAlt:
      "Server infrastructure inside an African sovereign cloud data centre with blinking rack lights.",
    quote:
      "Their sovereign cloud migration gave us sub-50ms latency across all our West African branches. Compliance headaches are a thing of the past.",
    name: "Emeka Nwosu",
    role: "VP Engineering, PanAfrica Logistics",
    ctaLabel: "See cloud solutions",
    ctaHref: "#",
  },
  {
    id: 2,
    imgDesktop:
      "https://images.unsplash.com/photo-1550751827-4bd374c3f58b?w=2000&auto=format&fit=crop&q=80",
    imgMobile:
      "https://images.unsplash.com/photo-1550751827-4bd374c3f58b?w=750&auto=format&fit=crop&q=80",
    imgAlt:
      "Cybersecurity analyst monitoring threat dashboards with encryption indicators across multiple displays.",
    quote:
      "CILPRON's HSM encryption and NDPA audit trail gave our board the confidence to launch our digital lending product. Bank-grade security, delivered on time.",
    name: "Funke Adeyemi",
    role: "Head of Compliance, CreditPulse",
    ctaLabel: "See security services",
    ctaHref: "#",
  },
  {
    id: 3,
    imgDesktop:
      "https://images.unsplash.com/photo-1677442135703-1787eea5ce01?w=2000&auto=format&fit=crop&q=80",
    imgMobile:
      "https://images.unsplash.com/photo-1677442135703-1787eea5ce01?w=750&auto=format&fit=crop&q=80",
    imgAlt:
      "AI automation dashboard showing agentic workflow runners processing invoice queues autonomously.",
    quote:
      "Their CAEL AI engine now handles 80% of our invoice reconciliation without any human touch. We redeployed an entire back-office team to growth roles.",
    name: "Chidi Eze",
    role: "COO, Meridian Insurance Group",
    ctaLabel: "Discover agentic AI",
    ctaHref: "/artificial-intelligence",
  },
  {
    id: 4,
    imgDesktop:
      "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=2000&auto=format&fit=crop&q=80",
    imgMobile:
      "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=750&auto=format&fit=crop&q=80",
    imgAlt:
      "Hospital administrator using an enterprise portal on a tablet for patient booking and HMO claims.",
    quote:
      "The CILPRON HMO portal cut our claims authorization time from 3 days to under 4 hours. Our patients notice the difference every single visit.",
    name: "Dr. Ngozi Mba",
    role: "Medical Director, WellCare HMO",
    ctaLabel: "View enterprise portals",
    ctaHref: "#",
  },
];

const AUTO_INTERVAL = 6000;
const wrap = (i: number) => ((i % slides.length) + slides.length) % slides.length;

// Quote font-size scale — long quotes get shrunk so they never overflow the
// fixed-height slide (unchanged from before, still needed).
const getQuoteScale = (quote: string) => {
  const len = quote.length;
  if (len <= 95) return 1;
  if (len <= 130) return 0.86;
  if (len <= 165) return 0.74;
  return 0.64;
};

// Signed, wrapped distance of slide i from the active slide, matching
// Adobe's --slide-spread-sign: 0 for active, -1/+1 for immediate neighbors,
// -2/+2 for the next ones out, etc. This is what replaces the old separate
// "peek" DOM elements — one number per slide drives its whole transform.
const getSpread = (i: number, current: number, n: number) => {
  let diff = i - current;
  diff = ((diff % n) + n) % n; // normalize to 0..n-1
  if (diff > n / 2) diff -= n; // fold into the shortest signed distance
  return diff;
};

const HeroSectionPages: React.FC = () => {
  const [current, setCurrent] = useState(0);
  const [animKey, setAnimKey] = useState(0);
  const [progress, setProgress] = useState(0);

  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const progressRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const goTo = useCallback((index: number) => {
    const next = wrap(index);
    setCurrent(next);
    setAnimKey((k) => k + 1);
    setProgress(0);
  }, []);

  const startAuto = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (progressRef.current) clearInterval(progressRef.current);
    setProgress(0);

    const tick = 50;
    progressRef.current = setInterval(() => {
      setProgress((p) => Math.min(p + (tick / AUTO_INTERVAL) * 100, 100));
    }, tick);

    timerRef.current = setInterval(() => {
      setCurrent((c) => wrap(c + 1));
      setAnimKey((k) => k + 1);
      setProgress(0);
    }, AUTO_INTERVAL);
  }, []);

  useEffect(() => {
    startAuto();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (progressRef.current) clearInterval(progressRef.current);
    };
  }, [startAuto]);

  const handleNav = (dir: number) => {
    goTo(current + dir);
    startAuto();
  };

  const handleDot = (i: number) => {
    goTo(i);
    startAuto();
  };

  return (
    <section
      className="cilpron-hero"
      aria-label="CILPRON LIMITED client testimonials"
      aria-roledescription="carousel"
    >
      <div className="cilpron-hero__viewport">
        <button
          className="cilpron-hero__nav cilpron-hero__nav--prev"
          onClick={() => handleNav(-1)}
          aria-label="Previous slide"
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
            <path
              d="M7.5 2.5L4 6l3.5 3.5"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        <button
          className="cilpron-hero__nav cilpron-hero__nav--next"
          onClick={() => handleNav(1)}
          aria-label="Next slide"
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
            <path
              d="M4.5 2.5L8 6l-3.5 3.5"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        <div className="cilpron-hero__track">
          {slides.map((slide, i) => {
            const spread = getSpread(i, current, slides.length);
            const isActive = spread === 0;

            return (
              <div
                key={slide.id}
                className="cilpron-hero__slide"
                data-active={isActive}
                data-spread-abs={Math.abs(spread) >= 2 ? "2" : String(Math.abs(spread))}
                style={{ "--spread": spread } as React.CSSProperties}
                onClick={() => !isActive && goTo(i)}
                aria-hidden={!isActive}
              >
                <div className="cilpron-hero__bg">
                  <picture>
                    <source media="(min-width: 768px)" srcSet={slide.imgDesktop} />
                    <img
                      src={slide.imgMobile}
                      alt={isActive ? slide.imgAlt : ""}
                      className="cilpron-hero__bg-img"
                      loading={isActive ? "eager" : "lazy"}
                    />
                  </picture>
                  <div className="cilpron-hero__scrim" />
                  {isActive && (
                    <div
                      className="cilpron-hero__progress"
                      style={{ width: `${progress}%` }}
                      aria-hidden="true"
                    />
                  )}
                </div>

                <div className="cilpron-hero__content" key={isActive ? animKey : undefined}>
                  <blockquote
                    className="cilpron-hero__quote"
                    style={{ "--cilpron-quote-scale": getQuoteScale(slide.quote) } as React.CSSProperties}
                  >
                    {slide.quote}
                  </blockquote>

                  <div className="cilpron-hero__meta">
                    <p className="cilpron-hero__name">{slide.name}</p>
                    <p className="cilpron-hero__role">{slide.role}</p>
                  </div>

                  <a
                    href={slide.ctaHref}
                    className="cilpron-hero__cta"
                    tabIndex={isActive ? 0 : -1}
                  >
                    {slide.ctaLabel}
                  </a>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <ol className="cilpron-hero__dots" aria-label="Slide indicators">
        {slides.map((s, i) => (
          <li key={s.id}>
            <button
              className={`cilpron-hero__dot${i === current ? " cilpron-hero__dot--active" : ""}`}
              onClick={() => handleDot(i)}
              aria-label={`Go to slide ${i + 1} of ${slides.length}`}
              aria-current={i === current ? "true" : undefined}
            />
          </li>
        ))}
      </ol>

      <div aria-live="polite" aria-atomic="true" className="cilpron-hero__live">
        {`Slide ${current + 1} of ${slides.length} — ${slides[current].name}, ${slides[current].role}`}
      </div>
    </section>
  );
};

export default HeroSectionPages;